import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

export default function Account({ token, me, setMe }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [mobile, setMobile] = useState(() => (me && me.mobile) || '');
  useEffect(() => { setMobile((me && me.mobile) || ''); }, [me]);
  // Keep profile fields in sync with server (e.g. after admin edits name/email)
  useEffect(() => {
    if (!token || typeof setMe !== 'function') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        const user = data?.user ?? (data?.id ? data : null);
        if (!cancelled && user) setMe(user);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [token, setMe]);
  const normalizePhone = (s) => {
    if (typeof s !== 'string') return '';
    let t = s.trim();
    if (!t) return '';
    const hasPlus = t.startsWith('+');
    t = t.replace(/[^0-9+]/g, '');
    t = t.replace(/\+/g, '');
    if (hasPlus) t = '+' + t;
    if (t.startsWith('+') && t.length > 16) t = t.slice(0, 16);
    if (!t.startsWith('+') && t.length > 15) t = t.slice(0, 15);
    return t;
  };
  const save = async () => {
    if (!token) return;
    setSaving(true); setError(''); setToast('');
    try {
      const res = await fetch(`${API_BASE}/api/account`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mobile: normalizePhone(mobile) })
      });
      const ct = res.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await res.json() : {};
      if (!res.ok) throw new Error(data.error || 'Failed to update');
      if (data && data.user) {
        // Reflect canonical value from server (may be normalized or set to null)
        setMobile(data.user.mobile || '');
        if (typeof setMe === 'function') setMe(prev => (prev ? { ...prev, ...data.user } : data.user));
      }
      setToast('Profile updated');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };
  if (!token) return <Navigate to="/auth" replace />;
  if (me == null) {
    return (
      <div className="mx-auto max-w-xl px-4 py-8">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-xl px-4 py-8">
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
          <CardDescription>View your profile and update your mobile number.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1 text-sm">
            <div><span className="text-muted-foreground">Email:</span> {me.email}</div>
            <div>
              <span className="text-muted-foreground">Name:</span>{' '}
              {(me.first_name || '').trim()} {(me.last_name || '').trim()}
            </div>
            <div>
              <span className="text-muted-foreground">Member since:</span>{' '}
              {me.created_at ? new Date(me.created_at).toLocaleDateString() : '—'}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="account-mobile">Mobile</Label>
            <div className="flex flex-wrap gap-2">
              <Input
                id="account-mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="e.g., +15551234567"
                className="max-w-xs"
              />
              <Button onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          {toast && (
            <div
              role="status"
              aria-live="polite"
              className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400"
            >
              {toast}
            </div>
          )}

          <div className="text-sm">
            <Link to="/forgot-password" className="text-primary underline-offset-4 hover:underline">
              Reset password
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
